/**
 * RS / Archive Spiral — 3D spiral of photos driven by native scroll.
 *
 * Same architecture as RS / History: a sticky viewport inside a tall runway,
 * the page scroll position is the single source of truth (no preventDefault,
 * no hijacking — back button, restore and re-entry all behave natively).
 * Scroll progress maps to a Z travel; the world translates in Z and twists,
 * each tile fades by distance. Corner phrases advance by segment; the email
 * form reveals on the last one.
 *
 * Performance: tile geometry is computed once per resize (cached), the rAF
 * tick only writes transform/opacity and self-suspends when settled and via
 * IntersectionObserver when off screen.
 */
const START_Z = 420;
const FADE_NEAR = 30;
const FADE_SPAN = 140;
const FAR_CUT = 2800;
const FAR_SPAN = 4200;
const WORD_SWAP_MS = 380;

class RsArchiveSpiral extends HTMLElement {
  #raf = null;
  #observer = null;
  #visible = false;

  connectedCallback() {
    this.viewport = this.querySelector('[data-rs-viewport]');
    this.world = this.querySelector('[data-rs-world]');
    this.runway = this.querySelector('[data-rs-runway]');
    if (!this.viewport || !this.world || !this.runway) return;

    this.tiles = Array.from(this.world.querySelectorAll('[data-rs-tile]'));
    this.words = {
      tl: this.querySelector('[data-rs-word-tl]'),
      tr: this.querySelector('[data-rs-word-tr]'),
      bl: this.querySelector('[data-rs-word-bl]'),
      br: this.querySelector('[data-rs-word-br]'),
    };
    this.wordWrap = this.querySelector('[data-rs-words]');
    this.center = this.querySelector('[data-rs-center]');
    this.segLabel = this.querySelector('[data-rs-seg]');
    this.hint = this.querySelector('[data-rs-hint]');
    try {
      this.phrases = JSON.parse(this.querySelector('[data-rs-phrases]').textContent);
    } catch {
      this.phrases = [];
    }

    const d = this.dataset;
    this.stepZ = parseFloat(d.stepZ) || 230;
    this.stepDeg = parseFloat(d.stepDeg) || 34;
    this.radius = parseFloat(d.radius) || 0.3;
    this.tileScale = parseFloat(d.tileScale) || 1;
    this.twist = (parseFloat(d.twist) || 12) / 1000;
    this.lerp = parseFloat(d.fluid) || 0.07;
    this.forcedCenter = d.posted === 'true';

    this.cur = 0;
    this.shownSeg = -1;
    this.swapping = false;
    this.settled = false;

    this.#findScroller();
    this.#measure();
    this.onResize = () => {
      this.#findScroller();
      this.#measure();
    };
    window.addEventListener('resize', this.onResize);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#visible = entry.isIntersecting;
        if (entry.isIntersecting) this.#start();
        else this.#stop();
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.swapTimer);
  }

  /* Desktop scrolls .page-wrapper (html/body overflow:hidden >=990px), mobile
     scrolls the document; re-detected on resize (breakpoint can change live) */
  #findScroller() {
    this.scroller = null;
    let anc = this.parentElement;
    while (anc && anc !== document.body) {
      const oy = getComputedStyle(anc).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && anc.scrollHeight > anc.clientHeight) {
        this.scroller = anc;
        break;
      }
      anc = anc.parentElement;
    }
  }

  #scrollPos() {
    return this.scroller ? this.scroller.scrollTop : window.scrollY;
  }

  /* All layout reads and tile geometry live here; the tick only writes */
  #measure() {
    const rect = this.runway.getBoundingClientRect();
    const base = this.scroller ? this.scroller.getBoundingClientRect().top : 0;
    this.runwayTop = rect.top - base + this.#scrollPos();
    this.scrollRange = Math.max(1, this.runway.offsetHeight - this.viewport.offsetHeight);
    this.maxT = Math.max(1, START_Z + (this.tiles.length - 1) * this.stepZ - 140);

    const u = Math.min(this.viewport.clientWidth, this.viewport.clientHeight);
    const R = u * this.radius;
    const frac = (n) => n - Math.floor(n);
    this.tiles.forEach((el, i) => {
      const th = ((i * this.stepDeg - 70) * Math.PI) / 180;
      const r = R * (0.88 + 0.24 * frac(i * 0.618));
      const w = Math.max(30, Math.min(180, this.tileScale * Math.max(44, Math.min(122, u * (0.10 + 0.06 * frac(i * 0.377))))));
      const h = w * (0.78 + 0.52 * frac(i * 0.711));
      const z = START_Z + i * this.stepZ;
      el.dataset.z = z;
      el.style.left = `${Math.round(r * Math.cos(th) - w / 2)}px`;
      el.style.top = `${Math.round(r * Math.sin(th) * 0.92 - h / 2)}px`;
      el.style.width = `${Math.round(w)}px`;
      el.style.height = `${Math.round(h)}px`;
      el.style.transform = `translate3d(0, 0, ${-z}px)`;
    });
    this.settled = false;
  }

  #start() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = () => {
    this.#raf = requestAnimationFrame(this.#tick);
    const p = Math.min(1, Math.max(0, (this.#scrollPos() - this.runwayTop) / this.scrollRange));
    const target = p * this.maxT;
    const diff = target - this.cur;
    if (Math.abs(diff) < 0.05 && this.settled) return;
    this.settled = Math.abs(diff) < 0.05;
    this.cur += this.settled ? diff : diff * this.lerp;

    this.world.style.transform = `translateZ(${this.cur.toFixed(2)}px) rotateZ(${(this.cur * this.twist).toFixed(3)}deg)`;
    for (const el of this.tiles) {
      const dist = parseFloat(el.dataset.z) - this.cur;
      let op = Math.max(0, Math.min(1, (dist - FADE_NEAR) / FADE_SPAN));
      if (dist > FAR_CUT) op *= Math.max(0.18, 1 - (dist - FAR_CUT) / FAR_SPAN);
      el.style.opacity = op.toFixed(3);
      el.style.visibility = op < 0.02 ? 'hidden' : '';
    }

    if (this.hint && this.cur > 80) this.hint.style.opacity = '0';

    const segCount = this.phrases.length;
    if (segCount === 0) return;
    const seg = Math.min(segCount - 1, Math.floor((this.cur / this.maxT) * segCount));
    if (seg !== this.shownSeg && !this.swapping) {
      this.swapping = true;
      if (this.wordWrap) this.wordWrap.style.opacity = '0';
      this.swapTimer = setTimeout(() => {
        const s2 = Math.min(segCount - 1, Math.floor((this.cur / this.maxT) * segCount));
        this.shownSeg = s2;
        const ph = this.phrases[s2] || {};
        for (const k of ['tl', 'tr', 'bl', 'br']) {
          if (this.words[k]) this.words[k].textContent = ph[k] || '';
        }
        if (this.segLabel) {
          this.segLabel.textContent = `${String(s2 + 1).padStart(2, '0')} / ${String(segCount).padStart(2, '0')}`;
        }
        const last = s2 === segCount - 1;
        if (this.center) this.center.classList.toggle('is-on', last || this.forcedCenter);
        if (this.wordWrap) this.wordWrap.style.opacity = '1';
        this.swapping = false;
      }, WORD_SWAP_MS);
    }
  };
}

if (!customElements.get('rs-archive-spiral')) {
  customElements.define('rs-archive-spiral', RsArchiveSpiral);
}
